/**
 * A picker value is either a remote URL string (already-uploaded asset), a rich
 * `{ url, thumbnail_url }` object, or a "pending upload" wrapper holding a local
 * File. Uploads are deferred until Generate so files aren't pushed to storage
 * for generations that get abandoned.
 */
export function isPendingUpload(v) {
    return !!v && typeof v === 'object' && v.__pending === true
}

export function makePendingUpload(file, mediaType) {
    return {
        __pending: true,
        file,
        previewUrl: URL.createObjectURL(file),
        mediaType,
        name: file.name,
    }
}

/** URL to render in a thumbnail slot; plain strings pass through as-is. */
export function pickerDisplayUrl(v) {
    if (!v) return null
    if (typeof v === 'string') return v
    if (isPendingUpload(v)) return v.previewUrl
    return v.thumbnail_url || v.url || null
}

/**
 * The dedicated thumbnail URL only, or null. Video pickers use this to choose
 * between `<img>` (thumbnail known) and `<video>` (must render the asset).
 */
export function pickerThumbnailUrl(v) {
    if (!v) return null
    if (typeof v === 'string') return null
    if (isPendingUpload(v)) return null
    return v.thumbnail_url || null
}

/** Canonical asset URL, used at submit time to flatten rich values. */
export function pickerSubmitUrl(v) {
    if (!v) return null
    if (typeof v === 'string') return v
    if (isPendingUpload(v)) return null
    return v.url || null
}

/**
 * Identity key for deduping elements-array entries: the canonical URL, or the
 * underlying `File` for pending uploads. `null` means "no stable identity yet"
 * — callers treat those as never-duplicate.
 */
export function pickerDedupeKey(v) {
    if (!v) return null
    if (typeof v === 'string') return v
    if (isPendingUpload(v)) return v.file
    return v.url || null
}

/** `incoming` values whose key isn't already in `existing` (or earlier in `incoming`). */
export function dedupeAgainst(existing, incoming) {
    const seen = new Set(existing.map(pickerDedupeKey).filter(k => k != null))
    const out = []
    for (const v of incoming) {
        const k = pickerDedupeKey(v)
        if (k != null && seen.has(k)) continue
        if (k != null) seen.add(k)
        out.push(v)
    }
    return out
}

export function isPickerValueFilled(v) {
    if (isPendingUpload(v)) return true
    if (typeof v === 'string') return v.trim() !== ''
    return !!pickerSubmitUrl(v)
}

export function isValueFilled(value, type) {
    if (value === null || value === undefined) return false
    switch (type) {
        case 'image':
        case 'video':
        case 'audio':
            return isPickerValueFilled(value)
        case 'text':
        case 'options':
        case 'character':
            return String(value).trim() !== ''
        case 'elements':
        case 'video_elements':
        case 'audio_elements':
            return Array.isArray(value) && value.length > 0 && value.every(isPickerValueFilled)
        case 'boolean':
            return typeof value === 'boolean'
        case 'voice_settings':
            return value != null && typeof value === 'object'
        default:
            return true
    }
}
